import { PageSpinner, useNav } from '@armhr/ui';
import { Alert, Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../utils/paths';

const isForbidden = (err: Error | null) =>
  (err as any)?.response?.status === 403;

// The SwipeClock Web Clock supports `enclosed`/`compact` params for a tighter,
// chrome-free layout that fits the narrow clock column.
const toCompactClockUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('enclosed', '1');
    parsed.searchParams.set('compact', '1');
    return parsed.toString();
  } catch {
    return url;
  }
};

const iframeStyle: React.CSSProperties = {
  background: 'white',
  width: '100%',
  height: '100%',
  border: 'none',
  display: 'block',
};

const SwipeclockTimecardPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setFluid } = useNav();

  const {
    data: swipeClockUrl,
    error: timecardError,
    loading: timecardLoading,
  } = useApiData<string>((api) => api.attendance.getTimecardRedirect());

  const {
    data: clockUrl,
    error: clockError,
    loading: clockLoading,
  } = useApiData<string>((api) => api.attendance.getClockRedirect());

  // Render edge-to-edge so the timecard iframe can own the full content height.
  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  if (!user?.is_swipeclock_enabled) {
    navigate(paths.dashboard);
    return null;
  }

  if (timecardLoading || clockLoading) {
    return <PageSpinner />;
  }

  if (isForbidden(timecardError) || isForbidden(clockError)) {
    navigate(paths.dashboard);
    return null;
  }

  if (timecardError || clockError || !clockUrl || !swipeClockUrl) {
    return (
      <Alert severity="error">
        An error occured while loading the attendance page. Please refresh and
        try again
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Helmet>
        <title>Time Card | Armhr</title>
        <meta
          name="description"
          content="View and manage your time card and attendance."
        />
      </Helmet>
      <Box sx={{ display: 'flex', px: 3, py: 2, alignItems: 'center' }}>
        <Typography variant="h2">Time Card</Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          px: 3,
          pb: 2,
          overflowY: { xs: 'auto', md: 'visible' },
        }}
      >
        {/* Clock: narrow secondary column */}
        <Box
          sx={{
            width: { xs: '100%', md: '30%' },
            flexShrink: 0,
            minHeight: { xs: 320, md: 0 },
            border: '1px solid #f1f3f4',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <iframe
            loading="lazy"
            src={toCompactClockUrl(clockUrl)}
            title="SwipeClock Clock"
            style={iframeStyle}
          />
        </Box>
        {/* Timecard: the focus of the page */}
        <Box
          sx={{
            width: { xs: '100%', md: '70%' },
            minHeight: { xs: 600, md: 0 },
            border: '1px solid #f1f3f4',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <iframe
            loading="lazy"
            src={swipeClockUrl}
            title="SwipeClock Timecard"
            style={iframeStyle}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SwipeclockTimecardPage;
